import { styled, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { type BrandIdentity, BrandLockup } from '../../molecules/BrandLockup';

type EntryLayoutWidth = 'narrow' | 'wide';

type EntryLayoutSurface = 'card' | 'plain';

type Props = {
  title: string;
  /** Product signature (logo, name, slogan) shown above the content. */
  brand?: BrandIdentity;
  /** One or two sentences under the title saying what the screen is for. */
  description?: ReactNode;
  /** `narrow` (400px) for a short form, `wide` (600px) for a list of choices. */
  width?: EntryLayoutWidth;
  /** `card` frames the content (forms); `plain` leaves it on the canvas when it holds its own cards. */
  surface?: EntryLayoutSurface;
  children?: ReactNode;
  'data-testid'?: string;
};

type OwnerState = {
  width: EntryLayoutWidth;
  surface: EntryLayoutSurface;
};

const MAX_WIDTHS: Record<EntryLayoutWidth, number> = {
  narrow: 50,
  wide: 75,
};

const Root = styled('main')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  minHeight: '100dvh',
  paddingInline: theme.spacing(2),
  paddingBlock: theme.spacing(4),
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  [theme.breakpoints.up('sm')]: {
    alignItems: 'center',
    paddingBlock: theme.spacing(6),
  },
}));

const Column = styled('div')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  width: '100%',
  maxWidth: theme.spacing(MAX_WIDTHS[ownerState.width]),
}));

const Surface = styled('section')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  ...(ownerState.surface === 'card' && {
    padding: theme.spacing(3, 2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(4),
    },
  }),
}));

const Heading = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const Description = styled('div')(({ theme }) => ({
  ...theme.typography.body1,
  color: theme.palette.text.secondary,
}));

const Title = styled(Typography)({
  textWrap: 'balance',
  overflowWrap: 'anywhere',
});

/**
 * Full-page frame for the screens reached before (or outside) the application shell:
 * sign-in, password recovery, workspace selection, OAuth callbacks.
 */
export function EntryLayout(props: Props) {
  const ownerState = { width: props.width ?? 'narrow', surface: props.surface ?? 'card' };

  return (
    <Root data-testid={props['data-testid'] ?? 'entry-layout'}>
      <Column ownerState={ownerState}>
        {props.brand ? (
          <BrandLockup
            size="large"
            align="center"
            name={props.brand.name}
            logo={props.brand.logo}
            tagline={props.brand.tagline}
          />
        ) : null}
        <Surface ownerState={ownerState}>
          <Heading>
            <Title variant="h1">{props.title}</Title>
            {props.description ? (
              <Description>{props.description}</Description>
            ) : null}
          </Heading>
          {props.children}
        </Surface>
      </Column>
    </Root>
  );
}

export type { Props as EntryLayoutProps };
